import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/staff/auth/AuthContext";
import { useUnread } from "@/staff/letters/UnreadContext";
import { useChatFeed } from "@/staff/chat/ChatContext";
import { useAcceptedCount } from "@/staff/quotes/AcceptedCountContext";
import { usePush } from "@/staff/push/PushContext";
import { Toast, useToast } from "@/components/ui";
import { activeSection, activeSub, visibleSections, type SubLink } from "./navConfig";
import "./app-header.css";

/**
 * Two-row app chrome for the authenticated staff console.
 *   Row 1 — practice sections (Leads, Jobs, Quotes).
 *   Row 2 — the active section's sub-links + a contextual create button.
 *
 * Active state is URL-derived (works on deep-link / refresh / back). All
 * navigation goes through the router (Link / useNavigate) so the editor's
 * unsaved-changes useBlocker keeps intercepting every nav click.
 */
export function AppHeader() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { unread } = useUnread();
  const { totalUnread: chatUnread } = useChatFeed();
  const { count: acceptedCount } = useAcceptedCount();
  const { leadsBadge } = usePush();
  // Staff see every section except the admin-only ones (Review).
  const sections = visibleSections(isAdmin);
  // Per-section badges: the count plus what the number means (aria text).
  // Sections not listed simply show no badge.
  const badges: Record<string, { count: number; unit: string }> = {
    // Actionable leads (new / to-schedule / overdue consultation) plus THIS
    // viewer's unseen consultation bookings — opening the Pipeline clears
    // the unseen part (see PushContext.markBookingsSeen).
    leads: { count: leadsBadge, unit: "to review" },
    letters: { count: unread?.total ?? 0, unit: "unread" },
    chat: { count: chatUnread, unit: "unread" },
    quotes: { count: acceptedCount ?? 0, unit: "unconverted" },
  };
  // Row-2 badges, keyed by sub path (currently just the accepted queue).
  const subBadges: Record<string, { count: number; unit: string }> = {
    "/staff/quotes/accepted": { count: acceptedCount ?? 0, unit: "unconverted" },
  };
  const section = activeSection(pathname, sections);
  const sub = section ? activeSub(section, pathname) : null;
  // No section matched (odd route): show the first section's subs, nothing active.
  const subsSection = section ?? sections[0];

  /** Contextual create: navigate to the active sub's own path carrying a flag
   *  the destination page reads to open its existing create flow. Routing it
   *  (rather than calling a handler) keeps the dirty-guard in play. */
  function onNew(s: SubLink) {
    navigate(s.path, { state: { create: true } });
  }

  function renderSubLink(link: SubLink) {
    // `sub` is only set when a section matched, so this alone is safe.
    const isActive = link.path === sub?.path;
    const badge = subBadges[link.path];
    return (
      <Link
        key={link.path}
        to={link.path}
        className={`apphdr__subitem${isActive ? " is-active" : ""}`}
        aria-current={isActive ? "page" : undefined}
      >
        {link.label}
        {badge && badge.count > 0 ? (
          <span
            className="apphdr__badge apphdr__badge--sub"
            aria-label={`${badge.count} ${badge.unit}`}
          >
            {badge.count > 99 ? "99+" : badge.count}
          </span>
        ) : null}
        <span className="apphdr__subitem-underline" aria-hidden="true" />
      </Link>
    );
  }

  return (
    <header className="apphdr">
      {/* Row 1 — sections */}
      <div className="apphdr__top">
        <Link to={sections[0]?.defaultPath ?? "/staff"} className="apphdr__brand" aria-label="Arkitectly">
          <svg width="30" height="12" viewBox="0 0 160 63.5" className="apphdr__mark" aria-hidden="true">
            <polygon points="155.6,54.98 105.2,4.58 54.8,4.58 105.2,54.98 155.6,54.98" fill="#90A4BA" />
            <polygon points="4.4,54.98 54.8,4.58 105.2,4.58 54.8,54.98 4.4,54.98" fill="#FFFFFF" />
          </svg>
          <span className="apphdr__wordmark">Arkitectly</span>
        </Link>

        <nav className="apphdr__sections" aria-label="Sections">
          {sections.map((s) => {
            const isActive = s.key === section?.key;
            const badge = badges[s.key];
            return (
              <Link
                key={s.key}
                to={s.defaultPath}
                className={`apphdr__section${isActive ? " is-active" : ""}`}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="apphdr__section-label">{s.label}</span>
                {badge && badge.count > 0 ? (
                  <span className="apphdr__badge" aria-label={`${badge.count} ${badge.unit}`}>
                    {badge.count > 99 ? "99+" : badge.count}
                  </span>
                ) : null}
                <span className="apphdr__section-underline" aria-hidden="true" />
              </Link>
            );
          })}
        </nav>

        <div className="apphdr__spacer" />
        <UserMenu />
      </div>

      {/* Row 2 — subs of the active section + contextual create. Links flagged
          align:"right" render in a second nav past the spacer, so occasional
          utilities sit apart from the everyday views. */}
      {subsSection ? (
        <div className="apphdr__sub">
          <nav className="apphdr__subs" aria-label={`${subsSection.label} views`}>
            {subsSection.subs.filter((l) => l.align !== "right").map(renderSubLink)}
          </nav>
          <div className="apphdr__spacer" />
          {subsSection.subs.some((l) => l.align === "right") ? (
            <nav className="apphdr__subs" aria-label={`${subsSection.label} utilities`}>
              {subsSection.subs.filter((l) => l.align === "right").map(renderSubLink)}
            </nav>
          ) : null}
          {sub?.newLabel || sub?.altNew ? (
            <div className="apphdr__new-group">
              {sub?.newLabel ? (
                <button
                  type="button"
                  className={`apphdr__new${sub.altNew ? " apphdr__new--secondary" : ""}`}
                  onClick={() => onNew(sub)}
                >
                  <span aria-hidden="true">+</span> {sub.newLabel}
                </button>
              ) : null}
              {sub?.altNew ? (
                <Link to={sub.altNew.path} className="apphdr__new">
                  <span aria-hidden="true">+</span> {sub.altNew.label}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const push = usePush();
  const { toast, showToast } = useToast(4500);
  const [open, setOpen] = useState(false);
  const label = user?.name || user?.email || "";

  // Must stay a direct click handler: Safari only shows the notification
  // permission prompt from a user gesture. Every outcome gets a toast — a
  // silently swallowed subscribe failure here once read as "enabled" while
  // no subscription existed, which is the worst possible failure mode for
  // something whose whole job is to fire when nobody is looking.
  function onTogglePush() {
    const enabling = !push.enabled;
    void (enabling ? push.enable() : push.disable())
      .then(() =>
        showToast(enabling ? "Notifications enabled on this Mac" : "Notifications disabled on this Mac"),
      )
      .catch((err: unknown) => {
        if (enabling && "Notification" in window && Notification.permission === "denied") {
          showToast(
            "Notifications are blocked for this site — allow them in Safari Settings → Websites → Notifications, then try again.",
            "warn",
          );
        } else {
          showToast(err instanceof Error ? err.message : "Could not update notifications.", "warn");
        }
      });
  }

  return (
    <div className="apphdr__user">
      <button
        type="button"
        className="apphdr__avatar"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
      >
        {initials(label)}
      </button>
      {open ? (
        <>
          <div className="apphdr__menu-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="apphdr__menu" role="menu">
            {label ? <p className="apphdr__menu-name">{label}</p> : null}
            {push.supported ? (
              <button
                type="button"
                role="menuitem"
                className="apphdr__menu-item"
                disabled={push.busy}
                onClick={onTogglePush}
              >
                {push.enabled ? "Disable notifications on this Mac" : "Enable notifications on this Mac"}
              </button>
            ) : null}
            <button type="button" role="menuitem" className="apphdr__menu-item" onClick={logout}>Sign out</button>
          </div>
        </>
      ) : null}
      <Toast toast={toast} />
    </div>
  );
}

function initials(nameOrEmail: string): string {
  const s = nameOrEmail.trim();
  if (!s) return "?";
  if (s.includes("@")) return s[0]!.toUpperCase();
  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]![0] : "";
  return (first + last).toUpperCase() || "?";
}
